import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { IBoxComponent, IBoxRatingComponent } from '@enuan/chat__utils';
import { FormRow, IFormRowProps } from './FormRow';
import { cleanBoxProps } from '../../../utils/cleanBoxProps';
import { styled } from '@mui/material/styles';
import { ReactComponent as Star } from '@/assets/star.svg';

type RatingProps = IBoxComponent<Omit<IBoxRatingComponent, 'type'>> &
	IFormRowProps & {
		value?: number;
		center?: boolean;
		disabled?: boolean;
		onChange?: (value: number) => void;
	};

export const Rating = styled(
	({
		// custom props
		help_label,
		error,
		error_label,
		required_label,
		label,
		onChange,
		disabled,

		// standard props
		id, //ignore it to avoid duplicate
		value = 0,
		className,
		total,
		center = true,
		required,
		..._props
	}: RatingProps) => {
		const props = cleanBoxProps(_props);

		const [tempScore, setTempScore] = useState(value);
		const [_value, setValue] = useState(value);
		useEffect(() => {
			setTempScore(value);
			setValue(value);
		}, [value]);

		const onClick = useCallback(
			(score: number) => () => {
				if (disabled) return;
				setValue(score);
				onChange?.(score);
			},
			[disabled, onChange],
		);

		const onItemHover = useCallback(
			(score: number) => () => {
				if (disabled) return;
				setTempScore(score);
			},
			[disabled],
		);

		const onItemLeave = useCallback(() => {
			if (disabled) return;
			setTempScore(_value);
		}, [disabled, _value]);

		const getCheckedStatus = useCallback(
			(itemScore: number) => {
				if (tempScore > 0) return itemScore <= tempScore;
				return itemScore <= _value;
			},
			[tempScore, _value],
		);

		return (
			<FormRow
				className={classNames('form__rating', className)}
				label={label}
				error={error}
				error_label={error_label}
				help_label={help_label}
				required={required}
				required_label={required_label}
				{...props}
			>
				<div className={classNames('rating', disabled && 'rating--disabled')}>
					{Array(total)
						.fill(1)
						.map((e, i) => {
							const itemScore = i + 1;
							const checked = getCheckedStatus(itemScore);

							return (
								<div
									key={i}
									className={classNames(
										`rating__item--${itemScore}`,
										'rating__item',
										checked && 'rating__item--checked',
										disabled && 'rating__item--disabled',
									)}
									onClick={onClick(itemScore)}
									onMouseEnter={onItemHover(itemScore)}
									onMouseLeave={onItemLeave}
								>
									<Star />
								</div>
							);
						})}
				</div>
			</FormRow>
		);
	},
	{ label: 'Rating', target: 'Rating-root' },
)({
	'& .rating': {
		display: 'flex',
		alignItems: 'center',
	},
	'& .rating__item': {
		cursor: 'pointer',
		height: '25px',
		width: '25px',
		'&:not(:last-child)': {
			marginRight: '0.2em',
		},
		'&.rating__item--disabled': {
			cursor: 'default',
		},
		'& svg': {
			fill: 'var(--form__rating__item_color)',
			transform: 'scaleX(-1)',
			height: '100%',
			width: '100%',
		},
		'&.rating__item--checked svg': {
			fill: 'var(--form__rating__item--checked_color)',
		},
	},
});
